import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import DesktopLayout from "@/components/layout/DesktopLayout";
import { ActivityFilters } from "@/components/activities/ActivityFilters";
import ActivityList from "@/components/activities/ActivityList";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Activity } from "@/components/activities/types";
import InspirationCard from "@/components/inspiration/InspirationCard";
import Hey from "@/lib/hey-utils";
import SearchFilters from "@/components/activities/SearchFilters";
import { useIsMobile } from "@/hooks/use-mobile";
import { nb } from "date-fns/locale";
import { useAnalytics } from "@/hooks/use-analytics";
import { FlutterUtils } from "@/hooks/useFlutterBridge";

// Mock categories
const categories = ["Cultural", "Adventure", "Food & Drink", "Relaxation", "Nightlife", "Nature", "Shopping", "Family-friendly"];
let _latest_queryId = 0;

// Top 50 travel destinations
const inspirations = [{
  "id": "1",
  "title": "Paris, France",
  "description": "Experience the romance and charm of the City of Lights with its iconic landmarks, world-class cuisine, and artistic heritage.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/62b8de0a-2d85-4353-a3ca-2f7bf169dbd7/KeAzDrOAslrXGyEBXMOhknlHVVrDEHcwMXMHBmKEzF.jpg",
  "location": "Paris, France",
  "bestTimeToVisit": "Spring (April-June) or Fall (September-October)",
  "iataCode": "CDG",
  "asciiCityName": "Paris"
}, {
  "id": "2",
  "title": "Kyoto, Japan",
  "description": "Discover the perfect blend of ancient traditions and modern innovation in this historic city known for its temples, gardens, and cultural experiences.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/fcd9265a-f0b2-46ae-a1b5-3012c1898cd0/wUKdflFytopUwXmPAZpxgmztpqKxkCioyOzSbtFqyp.jpg",
  "location": "Kyoto, Japan",
  "bestTimeToVisit": "Spring (March-May) or Fall (October-November)",
  "iataCode": "KIX",
  "asciiCityName": "Osaka"
}, {
  "id": "3",
  "title": "Santorini, Greece",
  "description": "Escape to this stunning island paradise featuring white-washed buildings, blue-domed churches, and breathtaking sunset views over the Aegean Sea.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/98ee9c88-02d1-43e0-9d58-ae4751b1d943/CiDfmFUxfaYUTdVGhYSQzGUwthSBKnOBCHovxdPDWP.jpg",
  "location": "Santorini, Greece",
  "bestTimeToVisit": "Late April to Early October",
  "iataCode": "JTR",
  "asciiCityName": "Firá"
}, {
  "id": "4",
  "title": "Bali, Indonesia",
  "description": "Find your balance in this tropical paradise with stunning beaches, lush rice terraces, spiritual temples, and vibrant cultural scenes.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/f73b590c-1eca-43d5-bf1f-2a621c17213f/HjRoEBtUDjecLiFjWBpYFuRqxOwCHTZqTiBhscMpyE.jpg",
  "location": "Bali, Indonesia",
  "bestTimeToVisit": "April to October (Dry Season)",
  "iataCode": "DPS",
  "asciiCityName": "Denpasar"
}, {
  "id": "5",
  "title": "Barcelona, Spain",
  "description": "Immerse yourself in the creative spirit of this vibrant city with its unique architecture, Mediterranean beaches, and world-class dining scene.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/5e7c00a1-f47a-4dd2-aea7-7a8fce4d20c7/BhnLhSyaCCiSuvuKTxLVxviLRToMPkWVAmDEzaAbmu.jpg",
  "location": "Barcelona, Spain",
  "bestTimeToVisit": "May to June or September to October",
  "iataCode": "BCN",
  "asciiCityName": "Barcelona"
}, {
  "id": "6",
  "title": "New York City, USA",
  "description": "Experience the energy of the city that never sleeps with its iconic skyline, diverse neighborhoods, world-class museums, and Broadway shows.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/a2c733c9-2b4e-41ce-84e5-bbeda365b78e/xqtpQIpcZyakQyShFKHGAEVsRLsNcVqBavWzCKCaLd.jpg",
  "location": "New York City, USA",
  "bestTimeToVisit": "April to June or September to Early November",
  "iataCode": "JFK",
  "asciiCityName": "New York City"
}, {
  "id": "7",
  "title": "Cape Town, South Africa",
  "description": "Discover the natural beauty where mountains meet the ocean, along with rich cultural heritage, wildlife, and world-renowned vineyards.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/339eced0-dcd4-4d6d-a5d9-e7fc69648e02/EOSHrkEaqzrDELRMLykXYDZvLCMJkAPbhFJExZPPXD.jpg",
  "location": "Cape Town, South Africa",
  "bestTimeToVisit": "October to April",
  "iataCode": "CPT",
  "asciiCityName": "Cape Town"
}, {
  "id": "9",
  "title": "Tokyo, Japan",
  "description": "Dive into this ultra-modern metropolis that perfectly balances tradition and innovation, offering cutting-edge technology, amazing food, and cultural treasures.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/f87a3286-6391-46f5-8752-93a1976417e5/MfcaHmsfygLnMGiXbuZwpSFdAViXJGecGxbZKvAzOi.jpg",
  "location": "Tokyo, Japan",
  "bestTimeToVisit": "March to May or September to November",
  "iataCode": "HND",
  "asciiCityName": "Tokyo"
}, {
  "id": "10",
  "title": "Rome, Italy",
  "description": "Walk through living history in the Eternal City with its ancient ruins, Renaissance masterpieces, and world-famous cuisine.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/bfa4f887-9cdc-4912-a7d1-b3776ba22015/EfKStVstBHcmiKopWltbfmQQFGplcubaFawQVKpRpc.jpg",
  "location": "Rome, Italy",
  "bestTimeToVisit": "April to May or September to October",
  "iataCode": "FCO",
  "asciiCityName": "Rome"
}, {
  "id": "13",
  "title": "Sydney, Australia",
  "description": "Discover the perfect balance of urban excitement and natural beauty with its iconic harbor, stunning beaches, and vibrant cultural scene.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/d8c3dda5-a483-4bb4-b166-e84a32b3b8ff/jVYcJoKqMrmVrmSdcuKwlOzUmvRJnFVfAqfBDfNKpc.jpg",
  "location": "Sydney, Australia",
  "bestTimeToVisit": "September to November or March to May",
  "iataCode": "SYD",
  "asciiCityName": "Sydney"
}, {
  "id": "14",
  "title": "Marrakech, Morocco",
  "description": "Lose yourself in the sensory experience of this ancient city with its bustling souks, historic palaces, vibrant colors, and aromatic spices.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/3af03b74-12f3-408b-b424-5708abcceea2/jtKEJqteasMhzDlxspQwBykVysogKWkBEluvOSnuma.jpg",
  "location": "Marrakech, Morocco",
  "bestTimeToVisit": "March to May or September to November",
  "iataCode": "RAK",
  "asciiCityName": "Marrakesh"
}, {
  "id": "15",
  "title": "Prague, Czech Republic",
  "description": "Step into a fairy tale in this medieval city with its stunning architecture, charming Old Town, historic castle, and vibrant cultural scene.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/9bc22e0d-4080-4692-8edc-969719d324f4/uobcPPFWsRtKrStjpyAOpMTYzAPdAIEpvPTHfgzbAn.jpg",
  "location": "Prague, Czech Republic",
  "bestTimeToVisit": "March to May or September to November",
  "iataCode": "PRG",
  "asciiCityName": "Prague"
}, {
  "id": "16",
  "title": "Great Barrier Reef, Australia",
  "description": "Dive into the world's largest coral reef system with its incredible marine biodiversity, crystal-clear waters, and unforgettable underwater experiences.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/e5d3bd0c-6330-4768-8097-7dd0c89eaf03/ywBoojiPsxUALllZogRztFUFCjNDVLpaFHJyZPeBXp.jpg",
  "location": "Queensland, Australia",
  "bestTimeToVisit": "June to October",
  "iataCode": "CNS",
  "asciiCityName": "Cairns"
}, {
  "id": "17",
  "title": "Istanbul, Turkey",
  "description": "Bridge the gap between East and West in this historic metropolis with its stunning mosques, bustling bazaars, and rich cultural heritage.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/e21b0a94-4eb0-4c34-84f9-ba69e1f4eb54/tVVcnHEmufoXIuiGlzrsRChAaQukIrBDoIFdtVIvRh.jpg",
  "location": "Istanbul, Turkey",
  "bestTimeToVisit": "April to May or September to November",
  "iataCode": "IST",
  "asciiCityName": "Istanbul"
}, {
  "id": "19",
  "title": "Amsterdam, Netherlands",
  "description": "Cruise along picturesque canals in this charming city known for its historic architecture, world-class museums, and progressive culture.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/ecbd9825-cd98-4f9e-a54b-74c0db207ea1/wtqvnwMSjjYGqwKzniCdxYlKEhpzZuUPGhUYjvfron.jpg",
  "location": "Amsterdam, Netherlands",
  "bestTimeToVisit": "April to May or September to November",
  "iataCode": "AMS",
  "asciiCityName": "Amsterdam=Schiphol"
}, {
  "id": "20",
  "title": "Dubai, UAE",
  "description": "Experience the future in this ambitious desert metropolis with its record-breaking skyscrapers, luxury shopping, and extraordinary attractions.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/a51ee87d-d203-4000-8553-a9cab679094d/uTpQpkQnNZCDKsYajCNLhqgZxWkmTsjaHCQNDpjGnP.jpg",
  "location": "Dubai, UAE",
  "bestTimeToVisit": "November to March",
  "iataCode": "DXB",
  "asciiCityName": "Dubai"
}, {
  "id": "22",
  "title": "Venice, Italy",
  "description": "Float through time in this unique city built on water, famous for its romantic gondolas, stunning architecture, and artistic heritage.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/2beba195-9a59-4589-9cda-73d03e61bb09/xWeGigbHXnOEezxyahSVgKradVbarowTcDcfjoyhep.jpg",
  "location": "Venice, Italy",
  "bestTimeToVisit": "April to May or September to October",
  "iataCode": "VCE",
  "asciiCityName": "Venice"
}, {
  "id": "23",
  "title": "Hong Kong",
  "description": "Discover the perfect blend of East and West in this dynamic city with its stunning skyline, rich cultural traditions, and incredible food scene.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/678abe6d-5a9e-40a1-bf30-20480d6b50b6/SKMeDMvRzmkrLCnycJHbYNANyYCBnsDxhIgEIDQqNr.jpg",
  "location": "Hong Kong",
  "bestTimeToVisit": "October to December",
  "iataCode": "HKG",
  "asciiCityName": "Hong Kong"
}, {
  "id": "24",
  "title": "Petra, Jordan",
  "description": "Journey to this ancient marvel carved into rose-colored stone, with its impressive facades, hidden tombs, and rich historical significance.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/dae3468d-29ba-4288-b89e-12f2bc17795a/ckKKOVxnqDZFTFMitSLRMwzJxPRmnGjZBclFuYCGDc.jpg",
  "location": "Petra, Jordan",
  "bestTimeToVisit": "March to May or September to November",
  "iataCode": "AMM",
  "asciiCityName": "Amman"
}, {
  "id": "25",
  "title": "Queenstown, New Zealand",
  "description": "Embrace adventure in this stunning alpine town known for its breathtaking landscapes, outdoor activities, and adrenaline-pumping experiences.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/0c9a42f7-bb4f-42c5-8596-edeced481279/zrXffyJRybWznZnxNiPKtBhPJcvfbLRuNMoMfhTnZh.jpg",
  "location": "Queenstown, New Zealand",
  "bestTimeToVisit": "December to February or June to August for skiing",
  "iataCode": "ZQN",
  "asciiCityName": "Queenstown"
}, {
  "id": "26",
  "title": "Havana, Cuba",
  "description": "Step back in time in this vibrant city with its colorful architecture, vintage cars, lively music scene, and fascinating political history.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/0b23d8aa-bd8b-48e5-b2a2-4ab5cd31c97e/bvssTGxLskMJrwxMGMIbpYorsTkkMHQckqsoIxfjsU.jpg",
  "location": "Havana, Cuba",
  "bestTimeToVisit": "December to May",
  "iataCode": "HAV",
  "asciiCityName": "Havana"
}, {
  "id": "27",
  "title": "Budapest, Hungary",
  "description": "Discover the 'Pearl of the Danube' with its stunning architecture, thermal baths, rich history, and vibrant contemporary culture.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/85fe86c2-673d-4f97-98bc-c8de0c82fe90/MPkfFCrpFMfRPqhQJPYnaAUwdiloRrAwQVkuEfDmKi.jpg",
  "location": "Budapest, Hungary",
  "bestTimeToVisit": "March to May or September to November",
  "iataCode": "BUD",
  "asciiCityName": "Budapest"
}, {
  "id": "29",
  "title": "Dubrovnik, Croatia",
  "description": "Walk the ancient walls of this stunning medieval city with its marble streets, terracotta rooftops, and beautiful Adriatic setting.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/bc7ea6e4-a1b2-4835-9e74-1a12294bbeee/zvEnWxvhJrtzWtbKbdqtocxKCzXHiSlBjctdnSuorx.jpg",
  "location": "Dubrovnik, Croatia",
  "bestTimeToVisit": "May to June or September to October",
  "iataCode": "DBV",
  "asciiCityName": "Dubrovnik"
}, {
  "id": "30",
  "title": "Seoul, South Korea",
  "description": "Experience the dynamic energy of this modern metropolis with its cutting-edge technology, ancient palaces, vibrant street food, and K-pop culture.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/90c5e8e1-27e1-4274-aa08-1ad811534852/hnuWsNRJHkZAHfFBLtIocMNVgrERZpAhfQwkVOCloj.jpg",
  "location": "Seoul, South Korea",
  "bestTimeToVisit": "April to June or September to November",
  "iataCode": "ICN",
  "asciiCityName": "Incheon"
}, {
  "id": "31",
  "title": "Reykjavik, Iceland",
  "description": "Discover otherworldly landscapes with volcanic terrains, geothermal hot springs, stunning waterfalls, and the magical Northern Lights.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/b4625574-614f-4f28-9973-be0eca566cbb/EqjkjlMrvWXJJEOfHbrhhdUVqgJgUIsMWyHGfahJGh.jpg",
  "location": "Reykjavik, Iceland",
  "bestTimeToVisit": "June to August or September to March for Northern Lights",
  "iataCode": "KEF",
  "asciiCityName": "Reykjavík"
}, {
  "id": "32",
  "title": "Victoria Falls, Zambia/Zimbabwe",
  "description": "Feel the power of the 'Smoke that Thunders,' one of the world's largest and most spectacular waterfalls with its impressive curtain of water.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/efca7533-0879-4104-bb7d-e72883f11d87/RlYDddZsNMftoklFFdMYaUqNVYoTXfAxyEPragevoy.jpg",
  "location": "Victoria Falls, Zambia/Zimbabwe",
  "bestTimeToVisit": "February to May for maximum water flow",
  "iataCode": "VFA",
  "asciiCityName": "Victoria Falls"
}, {
  "id": "33",
  "title": "Florence, Italy",
  "description": "Immerse yourself in the birthplace of the Renaissance with its world-class art, stunning architecture, and delicious Tuscan cuisine.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/11fed09b-e858-421f-96ee-52467f35dd00/xbqsVgJeXXEDBEfWLApaAuaTdsjxrBziZwYayfquiq.jpg",
  "location": "Florence, Italy",
  "bestTimeToVisit": "April to June or September to October",
  "iataCode": "FLR",
  "asciiCityName": "Florence"
}, {
  "id": "34",
  "title": "Angkor Wat, Cambodia",
  "description": "Marvel at this ancient temple complex with its intricate carvings, historical significance, and stunning sunrise and sunset views.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/62bf372b-a04a-4cf8-b678-5d1eaa30e74c/XmqKafiFGMxoPvgiDTrHjrtoywMoDqUqlLWTfUicik.jpg",
  "location": "Siem Reap, Cambodia",
  "bestTimeToVisit": "November to February",
  "iataCode": "REP",
  "asciiCityName": "Siem Reap"
}, {
  "id": "35",
  "title": "Lisbon, Portugal",
  "description": "Enjoy the charm of this hilly coastal capital with its pastel-colored buildings, historic trams, delicious cuisine, and friendly atmosphere.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/2b47f2ce-29bc-48f5-b59b-4e0163690dd5/MCDdxpcrSNLwzecaeDuSoMOMOOLDenLmKzChfasKCD.jpg",
  "location": "Lisbon, Portugal",
  "bestTimeToVisit": "March to May or September to October",
  "iataCode": "LIS",
  "asciiCityName": "Lisbon"
}, {
  "id": "36",
  "title": "Swiss Alps, Switzerland",
  "description": "Experience breathtaking mountain scenery with snow-capped peaks, charming villages, excellent skiing, and pristine alpine lakes.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/23d505b9-dedb-4ed5-89fd-55d5aa9b2c64/nUJuRULqtCQtCZoQcOSwALPoRGwKvMXCdSLCWCmGjF.jpg",
  "location": "Swiss Alps, Switzerland",
  "bestTimeToVisit": "December to March for skiing, June to September for hiking",
  "iataCode": "ZRH",
  "asciiCityName": "Zürich"
}, {
  "id": "37",
  "title": "Jerusalem, Israel",
  "description": "Walk through millennia of history in this sacred city with its religious significance, ancient architecture, and cultural diversity.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/5e0cda95-35f2-45b0-9888-294922e585d5/mGiCaHGTSrSNWqKeCtNsEziEOZvOsoFbSdiJNQTvqa.jpg",
  "location": "Jerusalem, Israel",
  "bestTimeToVisit": "March to May or September to November",
  "iataCode": "TLV",
  "asciiCityName": "Tel Aviv"
}, {
  "id": "38",
  "title": "Buenos Aires, Argentina",
  "description": "Feel the passion in this lively city known for tango dancing, European architecture, rich cultural scene, and vibrant nightlife.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/96c24874-0a26-43a8-a10f-ba9f4cc10112/nWAqGMCVVJUBPschejVomhhQYimHRcuVcxrMlEGwKH.jpg",
  "location": "Buenos Aires, Argentina",
  "bestTimeToVisit": "March to May or September to November",
  "iataCode": "EZE",
  "asciiCityName": "Buenos Aires"
}, {
  "id": "39",
  "title": "Bora Bora, French Polynesia",
  "description": "Escape to this tropical paradise with crystal-clear waters, overwater bungalows, pristine beaches, and stunning coral reefs.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/868a0b14-4e48-459c-aa62-c2ee42fadfe8/UaefDnWnrhFlIlzBULPwJZFXDnYjbKUVYTCvMeatQQ.jpg",
  "location": "Bora Bora, French Polynesia",
  "bestTimeToVisit": "May to October",
  "iataCode": "BOB",
  "asciiCityName": "Bora Bora"
}, {
  "id": "40",
  "title": "Vienna, Austria",
  "description": "Immerse yourself in the elegance of this imperial city known for its classical music, magnificent palaces, coffee house culture, and artistic heritage.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/04cd5fe5-80e9-4b6b-892a-c321d0986f2b/MnjSMmFsVgAXFtjGwzJclSyqVbYDLtNrMWLqFMfJMb.jpg",
  "location": "Vienna, Austria",
  "bestTimeToVisit": "April to May or September to October",
  "iataCode": "VIE",
  "asciiCityName": "Vienna"
}, {
  "id": "41",
  "title": "San Francisco, USA",
  "description": "Cross the iconic Golden Gate Bridge and explore this scenic city with its rolling hills, historic cable cars, diverse neighborhoods, and rich cultural scene.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/5be53704-669a-44cf-9d4f-3c736123ee7f/wgRWcGsQNWdzBjpqSlPvqttEbssSODqbGUiEyhlhnA.jpg",
  "location": "San Francisco, USA",
  "bestTimeToVisit": "September to November",
  "iataCode": "SFO",
  "asciiCityName": "San Francisco"
}, {
  "id": "42",
  "title": "Chichén Itzá, Mexico",
  "description": "Step back into ancient Mayan civilization at this archaeological wonder with its impressive pyramids, sacred cenotes, and astronomical significance.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/3d0c1d8e-d7ac-4b64-8a5d-a03c46c64868/JnYYEpftcVJvAimgzRcKPFRgiZDYtxpjdXINMmzeTg.jpg",
  "location": "Yucatán, Mexico",
  "bestTimeToVisit": "November to March",
  "iataCode": "CUN",
  "asciiCityName": "Cancún"
}, {
  "id": "43",
  "title": "Cairo, Egypt",
  "description": "Journey to the land of the pharaohs and explore the ancient pyramids, the enigmatic Sphinx, and the rich cultural heritage of this historic city.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/4a366877-608f-45ca-ab52-5fd7dac5857d/dDtPscNflWkUpCyLEtVYUWlFQLnjJJJzxRJBktwRma.jpg",
  "location": "Cairo, Egypt",
  "bestTimeToVisit": "October to April",
  "iataCode": "CAI",
  "asciiCityName": "Cairo"
}, {
  "id": "44",
  "title": "Banff National Park, Canada",
  "description": "Experience the majesty of the Canadian Rockies with pristine wilderness, turquoise lakes, abundant wildlife, and year-round outdoor adventures.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/4ec9e1c5-5e50-41e7-a812-8397c7bfc67a/zCEHNIJbvLRgQeKukryuFcrRtaBhOWCzXBuwfGZFoQ.jpg",
  "location": "Alberta, Canada",
  "bestTimeToVisit": "June to August or December to March for skiing",
  "iataCode": "YYC",
  "asciiCityName": "Calgary"
}, {
  "id": "45",
  "title": "Stockholm, Sweden",
  "description": "Explore the 'Venice of the North' with its beautiful archipelago, historic old town, cutting-edge design, and progressive culture.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/0b8734ab-6879-446b-8cf6-dea43af6614e/EGRCbAsflhXypEOkKEkZyPlfdfcKAVkPdKRuurrVdB.jpg",
  "location": "Stockholm, Sweden",
  "bestTimeToVisit": "May to September",
  "iataCode": "ARN",
  "asciiCityName": "Stockholm"
}, {
  "id": "46",
  "title": "Plitvice Lakes, Croatia",
  "description": "Wander through this natural wonderland with its cascading waterfalls, sixteen interconnected lakes, and vibrant azure waters surrounded by lush forests.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/1814eb0c-9b51-4f2e-a031-c5d18cf0b990/PnnknprzzJCwnTwWlgTzVkSggVASSuTUEgWcUBwuOp.jpg",
  "location": "Plitvice Lakes, Croatia",
  "bestTimeToVisit": "May to June or September to October",
  "iataCode": "ZAG",
  "asciiCityName": "Zagreb"
}, {
  "id": "47",
  "title": "Cape Town, South Africa",
  "description": "Experience the breathtaking scenery where mountains meet the ocean, along with diverse culture, wildlife, and world-class vineyards.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/339eced0-dcd4-4d6d-a5d9-e7fc69648e02/EOSHrkEaqzrDELRMLykXYDZvLCMJkAPbhFJExZPPXD.jpg",
  "location": "Cape Town, South Africa",
  "bestTimeToVisit": "October to April",
  "iataCode": "CPT",
  "asciiCityName": "Cape Town"
}, {
  "id": "48",
  "title": "Singapore",
  "description": "Discover this futuristic garden city with its stunning architecture, multicultural heritage, impeccable cleanliness, and incredible food scene.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/fd00c4a7-a0e8-4456-83a8-f6ced53ed890/qdiKtRxxDXQyFIlrkkVYNstdwquyBZjoQdzUiAylKc.jpg",
  "location": "Singapore",
  "bestTimeToVisit": "February to April",
  "iataCode": "SIN",
  "asciiCityName": "Singapore"
}, {
  "id": "49",
  "title": "St. Petersburg, Russia",
  "description": "Explore the cultural capital of Russia with its stunning palaces, world-class museums, ornate churches, and beautiful canal network.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/57fad819-a5eb-4c08-9b57-9ae2bae2fb2f/EabWmwtamROTTzAaDlZvaLbpWkiVWSrmWfBSlKvAcC.jpg",
  "location": "St. Petersburg, Russia",
  "bestTimeToVisit": "May to July (White Nights) or September",
  "iataCode": "LED",
  "asciiCityName": "Saint Petersburg"
}, {
  "id": "50",
  "title": "Amalfi Coast, Italy",
  "description": "Drive along one of the world's most scenic coastlines with colorful cliffside villages, crystal-clear waters, and delicious Mediterranean cuisine.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/bef94288-5989-43b5-9a21-b4ecd8b2b4b9/EWLqkoCniHMIRoOAbGaVcwDEfmWPYNZdcMqJzOkPqJ.jpg",
  "location": "Amalfi Coast, Italy",
  "bestTimeToVisit": "May to June or September to October",
  "iataCode": "NAP",
  "asciiCityName": "Naples"
}, {
  "id": "52",
  "title": "Edinburgh, Scotland",
  "description": "Step into medieval history in this atmospheric city with its ancient castle, winding cobblestone streets, literary heritage, and lively festivals.",
  "imageUrl": "https://prod-spotpix-delivery.1a-lab.net/f86c63c5-2e68-4e73-9c1c-43ea3032670f/MTErvwJSpPfCIrEIoBavbCAMffcjKUtqjrMIOXPinu.jpg",
  "location": "Edinburgh, Scotland",
  "bestTimeToVisit": "May to September or December for Hogmanay",
  "iataCode": "EDI",
  "asciiCityName": "Edinburgh"
}];
const Discover = () => {
  const {
    t
  } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [cityName, setCityName] = useState("");
  const [displayCityName, setDisplayCityName] = useState("");
  const [iataCode, setIataCode] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [minPrice, setMinPrice] = useState<number>(10);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [radius, setRadius] = useState<number | undefined>(40);
  const isMobile = useIsMobile();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const {
    trackEvent,
    trackPageView
  } = useAnalytics();
  const isFlutterWebapp = FlutterUtils.isFlutterWebapp();

  // Track page view when component mounts
  useEffect(() => {
    trackPageView("/discover");
    trackEvent("PageView", "discover_page_view", "User viewed the Discover page");
  }, [trackPageView, trackEvent]);

  // Select 5 random destinations from the inspirations array
  const randomInspirations = useMemo(() => {
    const shuffled = [...inspirations].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 7);
  }, []);

  // use first inspi city name as default city name
  useEffect(() => {
    if (randomInspirations.length > 0) {
      setIataCode(randomInspirations[0].iataCode);
      trackEvent("Discover", "default_destination_set", `Default destination: ${randomInspirations[0].title} (${randomInspirations[0].iataCode})`);
    }
  }, [randomInspirations, trackEvent]);
  const fetchActivities = async () => {
    setIsLoading(true);
    try {
      const heyInstance = Hey.HeyUtils.getInstance();
      let interests = (selectedCategory ? selectedCategory : "") + " " + (searchQuery ? searchQuery : "");
      let location: any = {};
      if (cityName.trim().length > 0) {
        location.name = cityName.trim();
        setDisplayCityName(location.name);
      } else if (iataCode.trim().length > 0) {
        location.iata = iataCode.trim();
        const selectedInspiration = randomInspirations.find(item => item.iataCode === iataCode);
        if (selectedInspiration) {
          setDisplayCityName(selectedInspiration.location);
        }
      } else {
        console.error("No city name or IATA code provided.");
        trackEvent("Error", "missing_location", "No city name or IATA code provided for activity search");
        return;
      }
      _latest_queryId += 1;
      let current_queryId = _latest_queryId;

      // Track the activity search request
      trackEvent("Discover", "activity_search", `Location: ${displayCityName || location.name || location.iata}, Interests: ${interests.trim() || "none"}, Radius: ${radius || "default"}`);
      const response = await heyInstance.getActivities("inspiration-placeholder", {
        interests: interests.trim(),
        radius: radius?.toString(),
        minPrice,
        maxPrice,
        nbResults: 50,
        location: location
      });
      if (current_queryId !== _latest_queryId) {
        return;
      }

      // Track search results
      trackEvent("Discover", "activity_results", `Found ${response.results?.length || 0} activities for ${displayCityName || location.name || location.iata}`, response.results?.length);
      setActivities(response.results || []);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching activities:", error);
      trackEvent("Error", "activity_fetch_failed", `Failed to fetch activities: ${error instanceof Error ? error.message : String(error)}`);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchActivities();
  }, [iataCode, selectedCategory]);
  const handleSearch = () => {
    console.debug("Searching for activities with:", {
      query: searchQuery,
      city: cityName,
      category: selectedCategory,
      priceRange: {
        min: minPrice,
        max: maxPrice
      },
      radius
    });
    fetchActivities();
  };
  const handleCategorySelect = (category: string | undefined) => {
    if (category) {
      trackEvent("Discover", "category_select", `Selected category: ${category}`);
    } else {
      trackEvent("Discover", "category_clear", "Cleared category filter");
    }
    setSelectedCategory(category);
  };
  const handleInspirationSelect = (iata: string, location: string) => {
    trackEvent("Discover", "inspiration_select", `Selected destination: ${location} (${iata})`);
    setIataCode(iata);
    setCityName("");
  };
  return <DesktopLayout>
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className={`max-w-5xl mx-auto py-6 px-4 sm:px-6 space-y-8 ${isMobile ? 'pb-24' : ''}`}>
        <div className="flex flex-col space-y-2">
          <h1 className="text-2xl font-bold my-[20px]">{t("discover.title")}</h1>
          <p className="text-muted-foreground my-0">{t("discover.subtitle")}</p>
        </div>
        
        {/* Inspiration Section - Moved above search filters */}
        <div className="py-2">
          <h2 className="text-lg font-semibold mb-3">{t("discover.getInspired")}</h2>
          <div className="overflow-x-auto pb-4 hide-scrollbar">
            <div className="flex space-x-4 w-max">
              {randomInspirations.map(item => <div key={item.id} className="w-[300px] flex-shrink-0">
                  <InspirationCard id={item.id} title={item.title} description={item.description} imageUrl={item.imageUrl} location={item.location} bestTimeToVisit={item.bestTimeToVisit} iataCode={item.iataCode} setIataCode={iata => handleInspirationSelect(iata, item.location)} setCityName={setCityName} />
                </div>)}
            </div>
          </div>
        </div>
        
        {/* Search Filters */}
        <SearchFilters searchQuery={searchQuery} setSearchQuery={setSearchQuery} cityName={cityName} setCityName={setCityName} minPrice={minPrice} setMinPrice={setMinPrice} maxPrice={maxPrice} setMaxPrice={setMaxPrice} radius={radius} setRadius={setRadius} handleSearch={handleSearch} placeholderText={t("discover.searchPlaceholder")} />
        
        {/* Categories */}
        <div className="py-0">
          <h2 className="text-lg font-semibold mb-3">{t("discover.categoryTypes")}</h2>
          <ActivityFilters categories={categories} selectedCategory={selectedCategory} onSelectCategory={handleCategorySelect} />
        </div>
        
        {/* Popular Activities */}
        <div className="py-2">
          <h2 className="text-lg font-semibold mb-3">{t("discover.popularActivities")} in {displayCityName}</h2>
          <ActivityList activities={activities} isLoading={isLoading} />
        </div>
      </motion.div>
    </DesktopLayout>;
};
export default Discover;